// cache-warmup — eager prefetch of every read-policy endpoint the
// daily-use sections rely on, so list views still paint full data when
// the user wanders into them after losing network.
//
// Fires once on adult-shell mount (CacheWarmupMount in app.tsx) in
// parallel with the page's natural data loads. The wrapper in rpc.ts
// writes every successful response to IDB, so simply hitting an
// endpoint here populates the cache — no separate "store" calls.
//
// Deliberately NOT included:
//   - notes.read (individual file bodies) — kept on-demand; user
//     opening a file caches it for next time. Cold-warming every note
//     would multiply request count and bandwidth for files most users
//     never open.
//   - gallery.list / gallery.get — explicitly opted out (per user). The
//     gallery is browse-on-demand; offline gallery isn't a goal.
//   - recipes.get (per-recipe detail) — same reasoning as notes.read,
//     warmed when the user taps a card.
//
// Idempotent: re-running just re-fetches and re-writes cache. We
// throttle to once-per-session via a module-scoped flag so multiple
// mounts (HMR / route changes) don't stampede the server.

import { api } from "./rpc";
import { sortFlatNotes, walkRoot, writeWalkCache } from "./notes-walk";

let warmedThisSession = false;

// ── Helpers for the range-keyed endpoints ─────────────────────────

function isoLocal(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function currentMealplanWeek(): { from: string; to: string } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Monday-anchored week (Hub convention).
  const shift = (today.getDay() + 6) % 7;
  const monday = new Date(today);
  monday.setDate(monday.getDate() - shift);
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  return { from: isoLocal(monday), to: isoLocal(sunday) };
}

function currentCalendarRange(): { rangeStart: string; rangeEnd: string } {
  // Mirrors useCalendar's range: month padded ± 14 days so all three
  // calendar views share the warm data.
  const anchor = new Date();
  anchor.setHours(0, 0, 0, 0);
  const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  start.setDate(start.getDate() - 14);
  const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  end.setDate(end.getDate() + 14);
  end.setHours(23, 59, 59, 999);
  return { rangeStart: start.toISOString(), rangeEnd: end.toISOString() };
}

// ── Per-section warmers ───────────────────────────────────────────
// Each returns a Promise that resolves (never rejects — internal
// failures are swallowed). The wrapper in rpc.ts also writes to cache
// on success, so just calling these is the warm-the-cache act.

async function warmShopping(): Promise<void> {
  const lists = await api.shoppingListLists().catch(() => []);
  await Promise.allSettled(
    lists.filter((l) => !l.archived).map((l) => api.shoppingListItems(l.id)),
  );
}

async function warmTodos(): Promise<void> {
  const lists = await api.todoLists().catch(() => []);
  await Promise.allSettled(
    lists.filter((l) => !l.archived).map((l) => api.todoListItems(l.id)),
  );
}

async function warmNotes(): Promise<void> {
  const roots = await api.notesListRoots().catch(() => []);
  await Promise.allSettled(
    roots.map(async (r) => {
      const flat = await walkRoot(r.id);
      writeWalkCache(r.id, sortFlatNotes(flat));
    }),
  );
}

// ── Entry point ───────────────────────────────────────────────────

export async function warmCache(): Promise<void> {
  if (warmedThisSession) return;
  warmedThisSession = true;

  const range = currentCalendarRange();
  const week = currentMealplanWeek();

  // Fire everything in parallel. Promise.allSettled so a single
  // failure doesn't sink the others — each section is independent.
  await Promise.allSettled([
    api.recipesList().catch(() => {}),
    api.pantryList().catch(() => {}),
    api.calendarListEvents(range).catch(() => {}),
    api.mealplanListForRange(week.from, week.to).catch(() => {}),
    warmShopping(),
    warmTodos(),
    warmNotes(),
  ]);
}
